/** @jsxImportSource @emotion/react */

import { EuiCallOut, EuiLink, EuiSpacer, EuiText } from "@inscopix/ideas-eui";
import { captureException } from "@sentry/react";
import assert from "assert";

import { isNonNullish } from "utils/isNonNullish";
import { sleep } from "utils/sleep";
import { ToolParamsGridRowDatum, ToolPathParam } from "../ToolParamsGrid.types";
import { useToolParamsGridContext } from "../ToolParamsGridProvider";
import { ModalToolCellStatusParamWrapper } from "./ModalToolCellStatusParamWrapper";

interface ModalToolCellStatusParamNoSourceFileProps {
  sourceFileParam: ToolPathParam;
  onClose: () => void;
  rowDatum: ToolParamsGridRowDatum;
  readOnly?: boolean;
}

export const ModalToolCellStatusParamNoSourceFile = ({
  sourceFileParam,
  onClose,
  rowDatum,
  readOnly,
}: ModalToolCellStatusParamNoSourceFileProps) => {
  const { gridApi } = useToolParamsGridContext();

  return (
    <ModalToolCellStatusParamWrapper
      onClose={onClose}
      maxWidth="600px"
      readOnly={readOnly}
    >
      {!readOnly && (
        <>
          <EuiCallOut>
            <EuiText>
              <p>
                To view/edit cell statuses you must first select a cell set
                input file of the appropriate type in the{" "}
                <EuiLink
                  onClick={() =>
                    void (async () => {
                      try {
                        onClose();
                        gridApi?.ensureColumnVisible(
                          sourceFileParam.key,
                          "start",
                        );
                        // Wait a tick for the scroll to complete
                        await sleep(0);

                        const rowNode = gridApi?.getRowNode(rowDatum.id);

                        assert(isNonNullish(rowNode));
                        gridApi?.flashCells({
                          rowNodes: [rowNode],
                          columns: [sourceFileParam.key],
                        });
                      } catch (err) {
                        captureException(err);
                        // TODO: investigate the cause of this error
                        // https://github.com/ag-grid/ag-grid/issues/5085
                      }
                    })()
                  }
                >
                  {sourceFileParam.name}
                </EuiLink>{" "}
                column.
              </p>
            </EuiText>
          </EuiCallOut>
          <EuiSpacer />
        </>
      )}
    </ModalToolCellStatusParamWrapper>
  );
};
